import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

const DECODERS = 'https://cdn.jsdelivr.net/npm/three@0.165.0/examples/jsm/libs/';
let loader;
function getLoader(renderer) {
  if (loader) return loader;
  loader = new GLTFLoader();
  const draco = new DRACOLoader(); draco.setDecoderPath(DECODERS + 'draco/gltf/'); loader.setDRACOLoader(draco);
  const ktx2 = new KTX2Loader(); ktx2.setTranscoderPath(DECODERS + 'basis/'); ktx2.detectSupport(renderer); loader.setKTX2Loader(ktx2);
  loader.setMeshoptDecoder(MeshoptDecoder);
  return loader;
}

/**
 * Charge un .glb et renvoie la même interface que les générateurs procéduraux.
 * model: { src, fitHeight (unités : 1 mm = 0.01), rotation:[x,y,z] (deg),
 *          materialRoles: { frame:["regex"], back:[...], logo:[...] },
 *          foldAnimation: "NomDuClip", anchors: { id: { position, normal } } }
 * Les nœuds vides nommés « hs_<id> » dans le fichier deviennent aussi des ancres.
 */
export async function loadGLB(model, renderer, onProgress) {
  const gltf = await getLoader(renderer).loadAsync(model.src, e => e.total && onProgress?.(e.loaded / e.total));
  const scene = gltf.scene;
  if (model.rotation) scene.rotation.set(...model.rotation.map(d => THREE.MathUtils.degToRad(d)));
  // normalisation : centré, posé à la bonne hauteur
  const wrap = new THREE.Group(); wrap.add(scene);
  let box = new THREE.Box3().setFromObject(wrap);
  const size = box.getSize(new THREE.Vector3());
  const k = model.fitHeight ? model.fitHeight / size.y : 1;
  scene.scale.multiplyScalar(k);
  box = new THREE.Box3().setFromObject(wrap);
  const c = box.getCenter(new THREE.Vector3());
  scene.position.sub(c);
  const root = new THREE.Group(); root.add(wrap);
  const fs = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3());

  scene.traverse(o => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = false; } });

  // rôles de matériaux (pour recolorer selon le coloris)
  const roles = {};
  const allMats = new Set(); scene.traverse(o => o.isMesh && [].concat(o.material).forEach(m => allMats.add(m)));
  for (const [role, patterns] of Object.entries(model.materialRoles || {})) {
    const re = patterns.map(p => new RegExp(p, 'i'));
    roles[role] = [...allMats].filter(m => re.some(r => r.test(m.name || '')));
  }

  // ancres
  const anchors = new Map();
  scene.traverse(o => {
    if (/^hs_/.test(o.name)) { o.userData.n = (o.userData.n || new THREE.Vector3(0, 0, 1)); anchors.set(o.name.slice(3), o); }
  });
  for (const [id, a] of Object.entries(model.anchors || {})) {
    const o = new THREE.Object3D(); o.position.set(...a.position); o.userData.n = new THREE.Vector3(...(a.normal || [0, 0, 1])).normalize();
    root.add(o); anchors.set(id, o);
  }

  // variantes intégrées (KHR_materials_variants)
  const parser = gltf.parser;
  const variantNames = (parser.json.extensions?.KHR_materials_variants?.variants || []).map(v => v.name);
  async function selectVariant(name) {
    const idx = variantNames.indexOf(name); if (idx < 0) return false;
    const jobs = [];
    scene.traverse(o => {
      const ext = o.isMesh && o.userData.gltfExtensions?.KHR_materials_variants; if (!ext) return;
      if (!o.userData.originalMaterial) o.userData.originalMaterial = o.material;
      const mapping = ext.mappings.find(m => m.variants.includes(idx));
      if (mapping) jobs.push(parser.getDependency('material', mapping.material).then(m => { o.material = m; parser.assignFinalMaterial(o); }));
      else o.material = o.userData.originalMaterial;
    });
    await Promise.all(jobs); return true;
  }

  // pliage par clip d'animation
  let mixer = null, action = null, clip = null;
  if (model.foldAnimation) {
    clip = gltf.animations.find(a => a.name === model.foldAnimation);
    if (clip) { mixer = new THREE.AnimationMixer(scene); action = mixer.clipAction(clip); action.play(); action.paused = true; }
  }

  return {
    root, anchors, roles, effects: {}, screen: null,
    size: { W: fs.x, H: fs.y },
    variantNames, selectVariant,
    canFold: !!action,
    setFold: t => { if (!action) return; action.time = (1 - t) * clip.duration; mixer.update(0); },
    animations: gltf.animations
  };
}
